import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const MANUAL = "__manual__";

export const QuoteWonDialog = ({ open, onOpenChange, quoteId, quoteTitle, onWon }) => {
  const [form, setForm] = useState({ customer_po_number: "", po_date: today(), po_value: "", contact_name: "", contact_email: "", notes: "" });
  const [pre, setPre] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pick, setPick] = useState(MANUAL);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {   // AUTO-FILL from the quote: PO value, contact the quote was given to, PO number already on record
    if (!open || !quoteId) return;
    let alive = true;
    setLoading(true);
    api.get(`/comms/quote-won/prefill/${quoteId}`).then(({ data }) => {
      if (!alive) return;
      setPre(data);
      const c = data.contact || {};
      const match = (data.contacts || []).find((x) => (c.email && x.email && x.email.toLowerCase() === c.email.toLowerCase()) || (c.id && x.id === c.id));
      setPick(match ? match.id : MANUAL);
      setForm((f) => ({
        ...f,
        customer_po_number: data.customer_po_number || f.customer_po_number,
        po_date: data.po_date || f.po_date,
        po_value: data.po_value ? String(Number(data.po_value).toFixed(2)) : f.po_value,
        contact_name: c.name || match?.name || f.contact_name,
        contact_email: c.email || match?.email || f.contact_email,
      }));
    }).catch(() => {}).finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [open, quoteId]);

  const choose = (e) => {
    const id = e.target.value;
    setPick(id);
    if (id === MANUAL) return;
    const c = (pre?.contacts || []).find((x) => x.id === id);
    if (c) setForm((f) => ({ ...f, contact_name: c.name || "", contact_email: c.email || "" }));
  };

  const submit = async () => {
    if (!form.customer_po_number.trim()) return toast.error("Customer PO number is required");
    if (!form.po_date) return toast.error("PO date is required");
    setBusy(true);
    try {
      const { data } = await api.post("/comms/quote-won", {
        quote_id: quoteId,
        customer_po_number: form.customer_po_number.trim(),
        po_date: form.po_date,
        po_value: form.po_value ? Number(form.po_value) : null,
        contact_name: form.contact_name || null,
        contact_email: form.contact_email || null,
        notes: form.notes || null,
      });
      onOpenChange(false);
      onWon?.(data);
      try { window.dispatchEvent(new CustomEvent("st:moments-refresh")); } catch { /* ignore */ }
      if (data.email_prepared) {
        toast.success("Quote marked as WON — Order Received email prepared for review", {
          description: `To: ${data.recipient || "recipient missing — fix in Outbox"}`,
          action: { label: "Review", onClick: () => navigate("/admin/communications") },
          duration: 8000,
        });
      } else {
        toast.success("Quote marked as WON — Customer PO recorded");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not mark quote as won");
    } finally {
      setBusy(false);
    }
  };

  const own = (pre?.contacts || []).filter((c) => !c.sister);
  const sister = (pre?.contacts || []).filter((c) => c.sister);
  const label = (c) => `${c.name || "—"}${c.designation ? ` · ${c.designation}` : ""}${c.email ? ` — ${c.email}` : " — no email"}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md z-[140]" data-testid="quote-won-dialog">
        <DialogHeader>
          <DialogTitle className="text-base">Mark as Won · PO Received</DialogTitle>
          <DialogDescription className="text-[12px]">
            {quoteTitle} — records the customer PO and prepares the Order Received email for your review (nothing sends automatically).
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-[11px] text-slate-500 inline-flex items-center gap-1.5" data-testid="quote-won-prefill-loading"><Loader2 className="h-3 w-3 animate-spin" /> Filling from the quote…</div>
        ) : pre && (
          <div className="text-[11px] text-emerald-700 inline-flex items-center gap-1.5" data-testid="quote-won-prefill-note">
            <Sparkles className="h-3 w-3" /> Filled from {pre.quote_code}: PO value {pre.currency} {Number(pre.quote_total_inr || 0).toLocaleString("en-IN")}{pre.contact?.name ? ` · quoted to ${pre.contact.name}` : ""}{pre.customer_po_number ? (pre.po_number_source === "ON_RECORD" ? ` · PO ${pre.customer_po_number} on record` : ` · PO ref ${pre.customer_po_number} taken from the quote subject — please check`) : ""}
          </div>
        )}
        <div className="space-y-2.5">
          <Field label="Customer PO number *">
            <Input value={form.customer_po_number} onChange={set("customer_po_number")}
              placeholder="e.g. ABC/PO/2026/0143" data-testid="quote-won-po-number" />
          </Field>
          <div className="grid grid-cols-2 gap-2.5">
            <Field label="PO date *">
              <Input type="date" value={form.po_date} onChange={set("po_date")} data-testid="quote-won-po-date" />
            </Field>
            <Field label={`PO value (${pre?.currency || "₹"})`}>
              <Input type="number" value={form.po_value} onChange={set("po_value")}
                placeholder="Quote total if blank" data-testid="quote-won-po-value" />
            </Field>
          </div>
          <Field label="Order placed by (team member)">
            <select value={pick} onChange={choose} data-testid="quote-won-contact-pick"
              className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-[13px] text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-600/30">
              {own.length > 0 && <optgroup label={pre?.customer_name || "Customer team"}>{own.map((c) => <option key={c.id} value={c.id}>{label(c)}</option>)}</optgroup>}
              {sister.length > 0 && <optgroup label="Group / sister companies">{sister.map((c) => <option key={c.id} value={c.id}>{label(c)} · {c.company}</option>)}</optgroup>}
              <option value={MANUAL}>Other — type name & email below</option>
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-2.5">
            <Field label="Contact name">
              <Input value={form.contact_name} onChange={(e) => { setPick(MANUAL); set("contact_name")(e); }} data-testid="quote-won-contact-name" />
            </Field>
            <Field label="Contact email">
              <Input type="email" value={form.contact_email} onChange={(e) => { setPick(MANUAL); set("contact_email")(e); }}
                placeholder="For the Order Received email" data-testid="quote-won-contact-email" />
            </Field>
          </div>
          <Field label="Notes">
            <Input value={form.notes} onChange={set("notes")} data-testid="quote-won-notes" />
          </Field>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} data-testid="quote-won-cancel">Cancel</Button>
          <Button size="sm" onClick={submit} disabled={busy}
            className="bg-emerald-700 hover:bg-emerald-800" data-testid="quote-won-confirm">
            {busy ? "Recording…" : "Confirm PO Received"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">{label}</div>
    {children}
  </div>
);

export default QuoteWonDialog;
